import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchAppSettings, patchAppSettings } from '../../services/appSettingsAPI';
import { getApiErrorMessage } from '../../services/errorUtils';
import './GeneralSettings.css';

const GeneralSettings: React.FC = () => {
  const navigate = useNavigate();
  const [minutes, setMinutes] = useState(15);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        setLoading(true);
        const settings = await fetchAppSettings();
        if (!cancelled) {
          setMinutes(settings.print_claim_window_minutes);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setError(getApiErrorMessage(err, 'Failed to load settings'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    setSavedMessage(null);
    try {
      const parsed = Number(minutes);
      if (!Number.isInteger(parsed) || parsed < 1 || parsed > 1440) {
        setError('Enter a whole number between 1 and 1440.');
        return;
      }
      const updated = await patchAppSettings(parsed);
      setMinutes(updated.print_claim_window_minutes);
      setSavedMessage('Saved.');
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to save settings'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="general-settings">
        <p className="general-settings-muted">Loading…</p>
      </div>
    );
  }

  return (
    <div className="general-settings">
      <button
        type="button"
        className="general-settings-back"
        onClick={() => navigate('/admin')}
      >
        Back to Admin Panel
      </button>
      <h1 className="general-settings-title">General Settings</h1>
      <label className="general-settings-field" htmlFor="print-claim-window-minutes">
        <span className="general-settings-label">Print Next claim window (minutes)</span>
        <input
          id="print-claim-window-minutes"
          type="number"
          min={1}
          max={1440}
          step={1}
          className="general-settings-input"
          value={minutes}
          onChange={(e) => { setMinutes(Number(e.target.value)); }}
        />
      </label>
      <button
        type="button"
        className="general-settings-save"
        onClick={() => { void handleSave(); }}
        disabled={saving}
      >
        {saving ? 'Saving…' : 'Save'}
      </button>
      {savedMessage ? (
        <p className="general-settings-success" role="status">{savedMessage}</p>
      ) : null}
      {error ? (
        <p className="general-settings-error" role="alert">{error}</p>
      ) : null}
    </div>
  );
};

export default GeneralSettings;
